/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useParams } from 'react-router-dom';
import { CTAdminBreadcrumbs } from '../../components/breadcrumbs/CTAdminBreadcrumbs';
import { CrossLinkCard, IntegrationsDashboard } from '@features/integrations';
import config from '../../../../config';
import { useToggle } from '@hooks/useToggle';
import { useAppSelector } from '@reducers/index';
import { getRootOrg, isRootOrg } from '@selectors/initiative';
import NotAuthorised from '@routes/not-authorised';
import { SubscriptionService } from '@services/SubscriptionService';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { materialityTrackerCardConfig } from '@constants/app-card';
import { createOnClickFromExternalUrl } from '@utils/click-handler';
import { MaterialityTrackerIntegrationModal } from './MaterialityTrackerIntegrationModal';
import { ProductCodes } from '../../../../types/products';

export const CompanyTrackerIntegrationsRoute = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const [openModal, toggleModal] = useToggle(false);

  const isRootOrganization = useAppSelector(isRootOrg);
  const rootOrg = useAppSelector(getRootOrg);

  if (!rootOrg || !isRootOrganization || initiativeId !== rootOrg._id) {
    return <NotAuthorised />;
  }

  const isMTIntegrated = SubscriptionService.hasActive(
    ProductCodes.MaterialityTracker,
    rootOrg.calculatedSubscriptions,
  );

  const materialityTrackerCard: CrossLinkCard = {
    key: 'materiality-tracker',
    logo: materialityTrackerCardConfig.logo,
    color: materialityTrackerCardConfig.color,
    name: 'Materiality Tracker',
    description: 'Stakeholder surveys and CSRD-aligned materiality mapping.',
    learnMoreUrl: config.brochureURL.materialityTracker,
    section: isMTIntegrated ? 'connected' : 'available',
    onOpen: isMTIntegrated
      ? createOnClickFromExternalUrl(generateUrl(ROUTES.MATERIALITY_TRACKER, { initiativeId }))
      : toggleModal,
  };

  return (
    <>
      <IntegrationsDashboard
        initiativeId={rootOrg._id}
        crossLinkCards={[materialityTrackerCard]}
        BreadCrumbsComponent={CTAdminBreadcrumbs}
      />
      <MaterialityTrackerIntegrationModal initiative={rootOrg} isOpen={openModal} toggle={toggleModal} />
    </>
  );
};
